import { useEffect, useRef } from 'react';
import { useQueryState } from 'nuqs';
import { useCopilotStatus } from '@customerio/hydra-copilot/react';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { useSidebarActive } from '@/features/sidebar';
import { useCopilotSidebar } from './use-copilot-sidebar';
import { isEmptyString } from '@/utils/string';
import { useIsAgentFullscreen } from './use-agent-fullscreen';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

/**
 * Manages the copilot's URL query parameters:
 *
 * - `ai-session` — Present in the URL while the copilot is visible (sidebar
 *   open or fullscreen page). Cleared when the copilot is hidden. On page
 *   load, restores the previous session and opens the copilot.
 * - `ai-message` — Prefills the chat input and opens the copilot. Consumed
 *   once on mount. Takes priority over `ai-session` when both are present.
 */
export function useCopilotSessionUrlSync(
  session: CopilotSession & { reset: () => CopilotSession },
  setInput: (value: string) => void,
) {
  const [sessionIdParameter, setSessionIdParameter] = useQueryState('ai-session');
  const [aiMessageParameter, setAiMessageParameter] = useQueryState('ai-message');
  const status = useCopilotStatus(session);
  const hasHandledMount = useRef(false);
  const isAgentFullscreen = useIsAgentFullscreen();
  const { openCopilot } = useCopilotSidebar();
  const isSidebarOpen = useSidebarActive('copilot');
  const isCopilotVisible = isAgentFullscreen || isSidebarOpen;

  // On mount: consume one-shot params and/or restore a previous session.
  useMountEffect(() => {
    if (hasHandledMount.current) {
      return;
    }
    hasHandledMount.current = true;

    // ai-message takes priority — prefill input and start a fresh chat
    if (!isEmptyString(aiMessageParameter)) {
      void setAiMessageParameter(null);
      setInput(aiMessageParameter);

      if (!isEmptyString(sessionIdParameter)) {
        void setSessionIdParameter(null);
      }

      if (!isAgentFullscreen) {
        openCopilot({ triggerSource: 'url_message' });
      }
      return;
    }

    // Restore session from URL
    if (!isEmptyString(sessionIdParameter)) {
      session
        .initialize(sessionIdParameter)
        .then(() => {
          if (!isAgentFullscreen) {
            openCopilot({ triggerSource: 'url_session' });
          }
        })
        .catch(() => {
          void setSessionIdParameter(null);
        });
    }
  });

  // Sync: when the copilot is visible and has an active session, ensure
  // the param is in the URL. When it's hidden or reset, clear it.
  // Skip while initializing to avoid a race where the param is cleared
  // before session.initialize() resolves.
  useEffect(() => {
    if (status === 'initializing') {
      return;
    }

    const currentId = session.id;

    // Read the actual URL to break a cross-app routing loop: Parcel's
    // use-query-params calls pushState on query-param changes, nuqs
    // intercepts those pushState calls and re-syncs its React state,
    // which can lag behind the real URL and cause repeated writes.
    // Comparing against the live URL prevents unnecessary updates.
    const actualSessionId = new URLSearchParams(window.location.search).get('ai-session');

    if (isCopilotVisible && status === 'ready' && currentId != null && sessionIdParameter !== currentId) {
      if (actualSessionId !== currentId) {
        void setSessionIdParameter(currentId);
      }
    }

    if ((!isCopilotVisible || (status === 'idle' && currentId == null)) && sessionIdParameter != null) {
      if (actualSessionId != null) {
        void setSessionIdParameter(null);
      }
    }
  }, [isCopilotVisible, status, session.id, sessionIdParameter, setSessionIdParameter]);
}
